import type { Middleware } from "@reduxjs/toolkit"

/**
 * A middleware slot the workflow canvas fills when its chunk loads.
 *
 * 🚨 THIS EXISTS BECAUSE `configureStore` FIXES ITS MIDDLEWARE CHAIN AT CREATION
 * AND THE STORE IS EAGER ON EVERY ROUTE. The undo middleware is built from the
 * workflow slice's own action creators, and that slice reaches `@xyflow/react`
 * for `addEdge`, `applyEdgeChanges` and `applyNodeChanges` — so naming the real
 * middleware in `configureStore` put the 172 kB canvas library (and its
 * stylesheet) in `index.html`'s modulepreload set for the public
 * `/embed/:embed-id` widget. There is no `replaceMiddleware`, so the indirection
 * is the only way to keep the slot without keeping the module.
 *
 * Before the canvas loads this forwards every action untouched, which is the
 * correct behaviour and not a degradation: undo history is a record of canvas
 * edits, and there is no canvas to edit yet.
 *
 * It takes a THUNK so the middleware is constructed on the first dispatched
 * action rather than while the registering module is still evaluating.
 */
let resolveInstalled: (() => Middleware) | null = null

export function installUndoMiddleware(resolve: () => Middleware): void {
  resolveInstalled = resolve
}

export const deferredUndoMiddleware: Middleware = (api) => (next) => {
  // Bound lazily and re-bound once the slot is filled: the store is created
  // before the canvas chunk exists, so `next` is captured here while
  // `resolveInstalled` is still null on every page that has a store.
  let boundTo: (() => Middleware) | null = null
  let chain: ((action: unknown) => unknown) | null = null

  return (action) => {
    if (resolveInstalled !== boundTo) {
      boundTo = resolveInstalled
      chain = resolveInstalled ? resolveInstalled()(api)(next) : null
    }
    return chain ? chain(action) : next(action)
  }
}
